import { useEffect, useRef, useState } from 'react';

import { useMap, AdvancedMarker } from '@vis.gl/react-google-maps';

import { findNonOverlappingPosition } from './DirectionsTooltipPositioning';

import { AQDirections, AQRoute, useSearchStore } from '@/store/SearchStore';
import { useIsScreenSize } from '@/utils/media';

export function Directions({ directions }: { directions: AQDirections }) {
  const { selectedRoute, highlightedRoute, setSelectedRoute, setHighlightedRoute } = useSearchStore();
  const map = useMap();
  const [polylines, setPolylines] = useState<google.maps.Polyline[]>([]);
  const zIndexRef = useRef<number>(0);
  const isMobile = useIsScreenSize('lt-lg');

  const COLORS: Record<string, string> = {
    green: '#007f1f',
    yellow: '#fff2bc',
    red: '#f3b6b6',
    white: '#fff',
    black: '#000',
    tonedDownGreen: '#e0f2e4',
    tonedDownYellow: '#fff9e6',
    tonedDownRed: '#fde8e8',
    gray: '#374151',
    blue: '#262DFF',
  };

  const getPolylineOptions = (route: AQRoute, type: 'background' | 'foreground'): google.maps.PolylineOptions => {
    const routeRanking = route.airQuality?.route_ranking ?? 1;
    const baseZ = 1000 - routeRanking * 10;
    const zOffset = type === 'foreground' ? 1 : 0;
    const isActive = (highlightedRoute && route === highlightedRoute) || (selectedRoute && route === selectedRoute);
    const boost = isActive ? 1000 : 0;
    const zIndex = baseZ + zOffset + boost;
    const base = {
      zIndex,
      visible: selectedRoute ? route === selectedRoute : true,
      icons: undefined,
    };
    if (type === 'background') {
      return {
        ...base,
        strokeColor: route.airQuality?.stroke || COLORS.white,
        strokeWeight: 10,
      };
    } else {
      return {
        ...base,
        strokeColor: route.airQuality?.color,
        strokeWeight: 6,
      };
    }
  };

  const handleClickRoute = (route: AQRoute) => {
    (isMobile ? setHighlightedRoute : setSelectedRoute)(route);
  };

  useEffect(() => {
    if (!map || !directions) return;

    polylines.forEach((polyline) => polyline.setMap(null));
    setPolylines([]);

    const newPolylines: google.maps.Polyline[] = [];
    directions.routes.forEach((route) => {
      const bgPolyline = new google.maps.Polyline({
        path: route.overview_path,
        ...getPolylineOptions(route, 'background'),
        map,
      });
      const fgPolyline = new google.maps.Polyline({
        path: route.overview_path,
        ...getPolylineOptions(route, 'foreground'),
        map,
      });
      fgPolyline.addListener('click', () => handleClickRoute(route));
      bgPolyline.addListener('click', () => handleClickRoute(route));
      newPolylines.push(bgPolyline, fgPolyline);
    });
    setPolylines(newPolylines);
    zIndexRef.current += 3;

    if (directions.routes.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      directions.routes.forEach((route) => {
        route.overview_path?.forEach((latLng) => bounds.extend(latLng));
      });
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 64);
      }
    }

    return () => {
      newPolylines.forEach((polyline) => polyline.setMap(null));
      setPolylines([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directions, map]);

  useEffect(() => {
    if (!polylines.length || !directions) return;
    polylines.forEach((polyline, index) => {
      const routeIndex = Math.floor(index / 2);
      const isForeground = index % 2 === 1;
      const route = directions.routes[routeIndex];
      if (!route) return;
      if ((highlightedRoute && route === highlightedRoute) || (selectedRoute && route === selectedRoute)) {
        zIndexRef.current += 3;
      }
      polyline.setOptions(getPolylineOptions(route, isForeground ? 'foreground' : 'background'));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute, highlightedRoute, polylines, directions]);

  if (!directions || !directions.routes.length || selectedRoute) return null;

  const generateMarkerSvg = (route: AQRoute): string => {
    const iconSize = 18;
    const textLineHeight = 20;
    const yPadding = 4;
    const xPadding = 8;
    const labelBgHeight = textLineHeight * 2 + yPadding;
    const totalHeight = iconSize / 2 + labelBgHeight;

    const rank = route.airQuality?.route_ranking ?? 1;
    const isHighlighted = highlightedRoute === route;
    const hasAirQualityData = route.airQuality?.in_modelled_area === 'Y';

    // Use route's actual color when highlighted, otherwise use toned-down colors
    let labelBackgroundColor;
    let textColor;

    if (!hasAirQualityData) {
      labelBackgroundColor = isMobile && isHighlighted ? COLORS.blue : route.airQuality?.color;
      textColor = COLORS.white;
    } else if (isMobile) {
      // Mobile: highlighted route uses actual color, others use toned-down colors
      if (isHighlighted) {
        labelBackgroundColor = route.airQuality?.color || COLORS.green;
        textColor = route.airQuality?.route_ranking === 1 ? COLORS.white : COLORS.black;
      } else {
        labelBackgroundColor =
          rank === 1
            ? COLORS.tonedDownGreen
            : rank === 2 && directions.routes.length === 3
              ? COLORS.tonedDownYellow
              : COLORS.tonedDownRed;
        textColor = COLORS.gray;
      }
    } else {
      labelBackgroundColor =
        rank === 1 ? COLORS.green : rank === 2 && directions.routes.length === 3 ? COLORS.yellow : COLORS.red;
      textColor = route.airQuality?.route_ranking === 1 ? COLORS.white : COLORS.black;
    }

    const percentage = Math.abs(route.airQuality?.percentage_bellow_highest?.no2 ?? 0);
    const labelRanking = [
      route.airQuality?.label_ranking,
      Number(route.airQuality?.route_ranking) < directions.routes.length && percentage > 0
        ? `(${percentage}% lower)`
        : '',
    ].join(' ');
    const labelDetails = `${route.airQuality?.label_duration || ''} (${route.airQuality?.label_distance || ''})`;

    const avgCharWidth = 6;
    const maxLength = Math.max(labelRanking.length, labelDetails.length);
    const estimatedTextWidth = maxLength * avgCharWidth;
    const rectWidth = estimatedTextWidth + 2 * xPadding;
    const rectX = 0;

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="${textColor}" width="${rectWidth}" height="${totalHeight}" viewBox="0 0 ${rectWidth} ${totalHeight}">

        <!-- Label Background (Route Color) -->
        <rect
          x="${rectX}"
          y="${iconSize / 2 - yPadding}"
          width="${rectWidth}" 
          height="${labelBgHeight + yPadding}"
          rx="6" ry="6"
          fill="${labelBackgroundColor}"
          filter="drop-shadow(0 1px 2px rgb(0 0 0 / 0.1)) drop-shadow(0 1px 1px rgb(0 0 0 / 0.06))" />

        <!-- Label Text Line 1 (Bold) -->
        <text
          x="${xPadding}" 
          y="${iconSize / 2 + textLineHeight * 0.75}"
          font-family="sans-serif"
          font-size="12"
          font-weight="bold"
          text-anchor="start">
            ${labelRanking}
        </text>

        <!-- Label Text Line 2 -->
        <text
          x="${xPadding}" 
          y="${iconSize / 2 + textLineHeight * 1.75}"
          font-family="sans-serif"
          font-size="12"
          text-anchor="start">
            ${labelDetails}
        </text>
      </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };

  return (
    <>
      {directions.routes.map((route, index) => {
        const position = findNonOverlappingPosition(route, index, directions);
        if (!position) return null;

        return (
          <AdvancedMarker key={index} position={position} onClick={() => handleClickRoute(route)}>
            <img src={generateMarkerSvg(route)} alt={`Route ${index + 1}`} />
          </AdvancedMarker>
        );
      })}
    </>
  );
}
